// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Select, Option } from 'informed';

import {
  addChildMeasurement,
  deleteChildMeasurement,
  updateChildMeasurement,
} from '../../../../actions/growthChartActions';

import DateText from '../../../DateText.jsx';
import NumericText from '../../../NumericText.jsx';
import Table from '../../../Table.jsx';
import { Error, Warning } from '../../../Typography.jsx';

type Props = {
  dob: string,
  measurements: Object[],
  activePatientId: string,
  addChildMeasurement: Function,
  deleteChildMeasurement: Function,
  updateChildMeasurement: Function,
}

class ChildMeasurementsComponent extends Component<Props> {
  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.date = moment(formValues.date, 'DD-MM-YYYY').toISOString();
  }

  transformMeasurements(formValues) {
    const {
      weightUnit, lengthUnit,
    } = formValues;
    if (weightUnit === 'lb') {
      formValues.weight = (formValues.weight / 2.2).toFixed(1);
    }
    if (lengthUnit === 'in') {
      formValues.length = (formValues.length / 0.3937).toFixed(1);
    }
  }

  handleDeleteRow = (id: string) => {
    this.props.deleteChildMeasurement(this.props.activePatientId, id);
  }

  showDelete = (row: Object) => row.showDelete !== false;

  handleSaveRow = (id: string, formData: Object) => {
    this.transformDates(formData.values);
    this.transformMeasurements(formData.values);
    this.props.updateChildMeasurement(this.props.activePatientId, {
      measurementId: id,
      ...formData.values,
    });
  }

  showEdit = (row: Object) => row.showEdit !== false;

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    this.props.addChildMeasurement(this.props.activePatientId, formData.values);
  }

  extraActions = (row: Object) => (row.showEdit === false ? '(from WCC)' : '');

  render() {
    const CRITICAL_LOW_PERCENTILE_THRESHOLD = 3;
    const LOW_PERCENTILE_THRESHOLD = 10;
    const CRITICAL_HIGH_PERCENTILE_THRESHOLD = 90;

    const columns = [
      {
        Header: 'Date',
        id: 'date',
        cell: item => (item.date ? moment(item.date).format() : '--'),
        editCell: item => <DateText field='date' item={item} />,
      },
      {
        Header: 'Age',
        id: 'ageString',
        defaultSortDesc: true,
        cell: item => (item.date && !item.overridden ? item.ageString : '--'),
        width: 80,
      },
      {
        Header: 'Weight',
        id: 'weight',
        cell: item => (item.weight ? `${item.weight} kg (${(item.weight * 2.2).toFixed(1)} lb)` : '--'),
        editCell: item => (
          <div>
            <NumericText field='weight' initialValue={item.weight} size={4} />
            <Select field='weightUnit'>
              <Option value='kg'>kg</Option>
              <Option value='lb'>lb</Option>
            </Select>
          </div>
        ),
      },
      {
        Header: 'Percentile',
        id: 'weightPercentile',
        cell: (item) => {
          if (item.weightPercentile === undefined || item.weightPercentile === null) {
            return '--';
          }
          if (item.weightPercentile < CRITICAL_LOW_PERCENTILE_THRESHOLD
            || item.weightPercentile > CRITICAL_HIGH_PERCENTILE_THRESHOLD) {
            return <Error>{item.weightPercentile}</Error>;
          }
          if (item.weightPercentile <= LOW_PERCENTILE_THRESHOLD) {
            return <Warning>{item.weightPercentile}</Warning>;
          }
          return item.weightPercentile;
        },
        width: 80,
      },
      {
        Header: 'Height',
        id: 'length',
        cell: item => (item.length ? `${item.length} cm (${(item.length / 2.54).toFixed(1)} in)` : '--'),
        editCell: item => (
          <div>
            <NumericText field='length' initialValue={item.length} size={4} />
            <Select field='lengthUnit'>
              <Option value='cm'>cm</Option>
              <Option value='in'>in</Option>
            </Select>
          </div>
        ),
      },
      {
        Header: 'Percentile',
        id: 'lengthPercentile',
        width: 80,
        cell: (item) => {
          if (item.lengthPercentile === undefined || item.lengthPercentile === null) {
            return '--';
          }
          if (item.lengthPercentile < CRITICAL_LOW_PERCENTILE_THRESHOLD
            || item.lengthPercentile > CRITICAL_HIGH_PERCENTILE_THRESHOLD) {
            return <Error>{item.lengthPercentile}</Error>;
          }
          if (item.lengthPercentile <= LOW_PERCENTILE_THRESHOLD) {
            return <Warning>{item.lengthPercentile}</Warning>;
          }
          return item.lengthPercentile;
        },
      },
    ];

    return (
      <Table
        columns={columns}
        data={this.props.measurements}
        handleDeleteRow={this.handleDeleteRow}
        handleSaveRow={this.handleSaveRow}
        handleSaveNewRow={this.handleSaveNewRow}
        showDelete={this.showDelete}
        showEdit={this.showEdit}
        extraActions={this.extraActions}
      />
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
});

const mapDispatchToProps = dispatch => ({
  addChildMeasurement:
    (patientId, measurement) => dispatch(addChildMeasurement(patientId, measurement)),
  deleteChildMeasurement:
    (patientId, measurementId) => dispatch(deleteChildMeasurement(patientId, measurementId)),
  updateChildMeasurement:
    (patientId, measurement) => dispatch(updateChildMeasurement(patientId, measurement)),
});

const ChildMeasurements = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChildMeasurementsComponent);

export default ChildMeasurements;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/growthChart/child/ChildMeasurements.jsx